import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";
import { HC } from "../hr/theme";
import { Card, SectionHead, Btn, Empty, Table, Field, Input, Pill } from "../hr/ui";
import { Sev, Spinner, Tabs } from "./mui";
import { Modal } from "./TasksChecklists";
import { toast } from "sonner";

export default function Escalations() {
  const { locationId } = useOutletContext();
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState(null);
  const [repeats, setRepeats] = useState(null);
  const [resolving, setResolving] = useState(null);
  const [notes, setNotes] = useState("");

  const load = useCallback(() => {
    api.get("/management/escalations", { params: { status, location_id: locationId || undefined } })
      .then(setRows).catch((e) => toast.error(formatApiError(e)));
    api.get("/management/repeated-misses", { params: { location_id: locationId || undefined } })
      .then(setRepeats).catch(() => {});
  }, [status, locationId]);

  useEffect(() => { load(); }, [load]);

  const resolve = async () => {
    if (!notes.trim()) return toast.error("Resolution notes are required");
    try {
      await api.post(`/management/escalations/${resolving.id}/resolve`, { notes });
      toast.success("Escalation resolved"); setResolving(null); setNotes(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!rows) return <Spinner />;

  return (
    <div className="space-y-5" data-testid="mgmt-escalations-page">
      <Card testid="escalations-card">
        <SectionHead title="Escalations" sub="Raised automatically when a task is missed"
          right={<Tabs testid="escalation-status-tabs" value={status} onChange={setStatus}
            tabs={[["open", "Open"], ["resolved", "Resolved"], ["all", "All"]]} />} />
        <Table testid="escalations-table" columns={[
          { key: "title", label: "Escalation" },
          { key: "assignee_name", label: "Assignee" },
          { key: "location_name", label: "Location" },
          { key: "severity", label: "Severity", render: (r) => <Sev value={r.severity} /> },
          { key: "level", label: "Level", align: "right" },
          { key: "created_at", label: "Raised", render: (r) => fmtDateTime(r.created_at) },
          { key: "status", label: "Status", render: (r) => <Pill status={r.status === "open" ? "pending" : "approved"} text={r.status} /> },
          { key: "act", label: "", align: "right", render: (r) => r.status === "open"
            ? <Btn variant="primary" onClick={() => setResolving(r)} data-testid={`escalation-resolve-${r.id}`}>Resolve</Btn>
            : <span className="text-[11px]" style={{ color: HC.soft }}>{r.resolution_notes}</span> },
        ]} rows={rows} empty="No escalations" />
      </Card>

      <Card testid="repeated-misses-card">
        <SectionHead title="Repeated-miss patterns"
          sub={repeats ? `${repeats.total_missed} missed in the last ${repeats.window_days} days · threshold ${repeats.threshold}` : ""} />
        {repeats?.patterns?.length ? (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {repeats.patterns.map((p, i) => (
              <div key={i} className="rounded-xl border p-3.5" style={{ borderColor: HC.border }} data-testid={`repeat-pattern-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-bold" style={{ color: HC.text }}>{p.label}</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: HC.redTint, color: HC.red }}>{p.count}×</span>
                </div>
                <div className="text-[11px] mt-1 uppercase font-bold tracking-wide" style={{ color: HC.soft }}>{p.kind.replace("_", " ")}</div>
                <div className="text-[12px] mt-1.5" style={{ color: HC.muted }}>{p.titles.join(", ")}</div>
              </div>
            ))}
          </div>
        ) : <Empty text="No repeated-miss patterns detected" />}
      </Card>

      {resolving && (
        <Modal title={`Resolve: ${resolving.title}`} onClose={() => { setResolving(null); setNotes(""); }}
          onSave={resolve} saveLabel="Resolve" testid="escalation-modal">
          <Field label="Resolution notes (required)">
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="escalation-notes-input" />
          </Field>
        </Modal>
      )}
    </div>
  );
}
